import { observer } from 'mobx-react'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { Side, Tab } from '@store/CodeView.store'
import { JSONEditor } from '@components/JSONEditor/JSONEditor'
import { getEntity } from '@communication/Configurations'
import { StoreContext } from '@components/App'
import { ContentLoader } from '../../TabbedCodeView.styles'
import { LifecycleState } from '@customTypes/General'
import { checkIfUserCanDeleteResource, checkIfUserCanEditResource } from '@communication/Permissions'
import { TabError } from '../TabError/TabError'

interface EntityWindowProps {
  side: Side
}

export const EntityWindow: FC<EntityWindowProps> = observer(({ side }) => {
  const store = useContext(StoreContext)
  const { windowsActiveTabs } = store.CodeViewState
  const tabData = useMemo(() => windowsActiveTabs[side] as Tab, [windowsActiveTabs, side])

  const [lifecycleState, setLifecycleState] = useState<LifecycleState>('init')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const fetchEntity = async () => {
    setLifecycleState('fetching')
    const res = await getEntity(tabData?.clientId)

    if (res?.success) {
      store.CodeViewState.setTabConfigurationData({ tabId: tabData?.id, configurationData: res?.data })
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, shouldRefetchData: false })
      setEditorKey((prev) => prev + 1)
      setLifecycleState('fetched')
    } else {
      setErrorMessage(res?.error?.message || 'Failed to fetch entity')
      setLifecycleState('error')
    }
  }

  const refreshTab = async () => {
    await fetchEntity()
    setEditorKey((prev) => prev + 1)
  }

  const checkEditRights = async () => {
    const canEditRes = await checkIfUserCanEditResource(tabData?.clientId)
    if (canEditRes?.data?.authorized) {
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, hasEditRights: true })
    }
  }

  const checkDeleteRights = async () => {
    const canDelete = await checkIfUserCanDeleteResource(tabData?.clientId)
    if (canDelete?.data?.authorized) {
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, hasDeleteRights: true })
    }
  }

  useEffect(() => {
    if (tabData?.configurationData?.current?.clientId && tabData?.clientId) {
      setEditorKey((prev) => prev + 1)
      setLifecycleState('fetched')
    } else if (tabData?.clientId) {
      fetchEntity()
      checkEditRights()
      checkDeleteRights()
    } else {
      setErrorMessage('Client ID not found')
      setLifecycleState('error')
    }
  }, [tabData?.clientId, tabData?.configurationData?.current?.clientId, tabData?.id])

  const handleJSONEditorChange = (e, val) => {
    if (val) {
      store.CodeViewState.setTabJsonData({ tabId: tabData?.id, jsonObject: val })
    }
  }

  const [editorKey, setEditorKey] = useState(0)

  useEffect(() => {
    setEditorKey((prev) => prev + 1)
  }, [tabData?.isEditing])

  useEffect(() => {
    if (tabData?.shouldRefetchData) {
      refreshTab()
    }
  }, [tabData?.shouldRefetchData])

  switch (lifecycleState) {
    case 'init':
      return <></>
    case 'fetching':
      return <ContentLoader type="code" />
    case 'fetched':
      return (
        <JSONEditor
          key={editorKey}
          height="100%"
          width="100%"
          value={tabData?.jsonObject}
          onChange={tabData?.isEditing ? handleJSONEditorChange : undefined}
        />
      )
    case 'error':
      return <TabError endpoint={'/configuration'} resourceType={tabData?.clientId} errorMessage={errorMessage} />
  }
})
